import { CreateCompanyEnrich } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import { senioritiesFromTitles } from "./senioritiesFromTitles";
import { TrackFilterValues, toPreviewRequest } from "./trackFilters";

/** How far the automatic ICP search had to be widened to find anybody. */
export type TrackRelaxation = "seniority" | "broadened";

export interface TrackSearchStep {
	filters: TrackFilterValues;
	request: CreateCompanyEnrich;
	/** `null` on the untouched ICP search — nothing was widened. */
	relaxation: TrackRelaxation | null;
}

const asStep = (
	filters: TrackFilterValues,
	relaxation: TrackRelaxation | null
): TrackSearchStep => ({ filters, relaxation, request: toPreviewRequest(filters) });

/** A step is worth running only if it filters on something and differs from the ones before it. */
const isWorthRunning = (step: TrackSearchStep, earlier: TrackSearchStep[]): boolean => {
	const body = JSON.stringify(step.request);

	return body !== "{}" && earlier.every((previous) => JSON.stringify(previous.request) !== body);
};

/**
 * The searches the Track step tries, in order, until one returns people.
 *
 * The ICP-derived search is the narrowest thing we ever run: it matches literal
 * job titles inside companies described by a sentence the chat wrote. Either
 * half can match nobody, which used to leave the user staring at an empty table
 * with no way to tell which filter was at fault (PREC-3719). So we widen:
 *
 * 1. the ICP search as confirmed;
 * 2. the job titles replaced by the seniority levels they denote;
 * 3. the same, with the company description dropped as well.
 *
 * Steps that would repeat an earlier request (no personas to drop, no
 * description to clear) or that would search on nothing at all are left out.
 */
export const buildTrackSearchLadder = (
	filters: TrackFilterValues,
	personas: string[] = []
): TrackSearchStep[] => {
	// The resolved titles first — they are what the search actually ran on. The
	// raw personas ("CEOs") are the fallback for a title that denotes no level.
	const derived = senioritiesFromTitles(filters.positionQuery.map((item) => item.title));

	const bySeniority: TrackFilterValues = {
		...filters,
		positionQuery: [],
		seniority: filters.seniority.length
			? filters.seniority
			: derived.length
				? derived
				: senioritiesFromTitles(personas),
	};

	const widenings: TrackSearchStep[] = [
		asStep(bySeniority, "seniority"),
		asStep({ ...bySeniority, semanticQuery: "" }, "broadened"),
	];

	// The confirmed ICP always runs — the widenings only when they add something.
	return widenings.reduce<TrackSearchStep[]>(
		(steps, step) => (isWorthRunning(step, steps) ? [...steps, step] : steps),
		[asStep(filters, null)]
	);
};

export interface TrackSearchOutcome<T> {
	step: TrackSearchStep;
	items: T[];
	totalItems: number;
}

/**
 * Runs the ladder until a step returns people. When every step comes back
 * empty, the outcome is the last one attempted — the widest search we tried,
 * which is what the filter pane should then open on.
 */
export const runTrackSearchLadder = async <T>(
	steps: TrackSearchStep[],
	search: (request: CreateCompanyEnrich) => Promise<{ items: T[]; totalItems: number }>
): Promise<TrackSearchOutcome<T>> => {
	let outcome: TrackSearchOutcome<T> = { step: steps[0], items: [], totalItems: 0 };

	for (const step of steps) {
		const { items, totalItems } = await search(step.request);

		outcome = { step, items, totalItems };

		if (items.length > 0) {
			return outcome;
		}
	}

	return outcome;
};
